import { useState, useRef, useContext, useCallback } from "react";
import { round, translate } from "../../../../utils/Common";
import AuthContext from "../../../../contexts/auth";
import ModalContext from "../../../../contexts/modal";
import { useForm } from "react-hook-form";
import { usePackages } from "../../../../hooks/usePackages";
import { FormGrid, Field } from "@/components/ui/form-field";
import { SwitchField } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
// icons
import * as MdIcons from "react-icons/md";
// components
import Tooltip from "../../../../components/Tooltip";
import FieldSizes from "./FieldSizes";
import LockerSizes from "./LockerSizes";
import DoorList from "../../../../components/DoorList";
import ProviderList from "../../../../components/ProviderList";
import PlatformSearchList from "../../../../components/PlatformSearchList";
import { IoMdCopy } from "react-icons/io";
import ClientsSearchList from "../../../../components/ClientsSearchList";

/**
 * The new package number, with a copy button.
 *
 * Its own component because it lives inside the success modal, whose body
 * `modalContext.confirm` captures once: state kept on the form re-rendered the
 * form, not the modal, so the "copied" confirmation never showed up.
 */
const CopyPackageNumber = ({ packageNumber }) => {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(packageNumber).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  return (
    <div className="mt-1 flex items-center gap-2">
      <span>
        {translate("global.numPackageIs")}{" "}
        <span className="font-semibold">{packageNumber}</span>
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={translate("global.copy")}
        onClick={copy}
      >
        <IoMdCopy size={20} />
      </Button>
      {copied && (
        <span className="text-sm font-medium text-success" role="status">
          {translate("global.copied")}
        </span>
      )}
    </div>
  );
};

// formulario para carga tipo B2B / Emprendedores
const FormB2BEmprendedores = (props) => {
  const userContext = useContext(AuthContext);
  const { createPackage } = usePackages();
  const userRole = JSON.parse(sessionStorage.getItem("user")).roleId;
  const modalContext = useContext(ModalContext);
  const {
    register,
    errors,
    clearErrors,
    setError,
    reset,
    handleSubmit,
    watch,
    setValue,
  } = useForm();

  // para saber qué unidades de medida utilizar --> 0 = cm/kg, 1 = inch/lb
  const [unidad, setUnidad] = useState(1);
  // para saber el tipo de ingreso de medidas
  const [ingresarMedidas, setIngresarMedidas] = useState(false);

  const [selectedClient, setSelectedClient] = useState(null);
  const [providor, setProvidor] = useState(null);
  const [platformSelect, setPlatformSelect] = useState(null);

  // para saber qué boxit se seleccionó
  const [isBoxit, setIsBoxit] = useState(false);
  const idPlatform = useRef({});
  idPlatform.current = watch("plataforma", "");
  const handleIsBoxit = useCallback((value) => setIsBoxit(value), []);

  // clear formulario
  const resetFormB2BEmprendedores = () => {
    reset();
    clearErrors();

    setIngresarMedidas(false);
    setUnidad(1);
    setIsBoxit(false);
    setPlatformSelect(null);
    setSelectedClient(null);
    setProvidor(null);
  };

  const [isSendingData, setIsSendingData] = useState(false);

  // registrar paquete en el servidor
  const sendPackage = async (data) => {
    try {
      if (!props?.canCreate) return;
      setIsSendingData(true);

      if (userContext.user?.IsAdmin) {
        if (!selectedClient) {
          setError("buscarCliente", {
            type: "",
            message: "Debes elegir un cliente válido.",
          });
          return;
        }
      }

      if (!userContext.user?.IsAdmin) {
        data.proveedor = userContext.user?.Ruc;
        data.cliente = userContext.user?.IdCliente;
        data.idTipoUsuario = userContext.user?.IdTipoUsuario;
      } else {
        data.proveedor = providor?.IdUsuario;
        data.cliente = selectedClient ? selectedClient : "";
        data.idTipoUsuario = providor?.IdTipoUsuario;
      }

      // validar datos a enviar
      if (data.puerta) {
        if (
          data.puerta === "No hay puertas disponibles" ||
          data.puerta === translate("global.select")
        )
          data.puerta = "";
      }
      if (unidad) {
        if (data.alto) data.alto = round(data.alto * 2.54, 2);
        if (data.largo) data.largo = round(data.largo * 2.54, 2);
        if (data.ancho) data.ancho = round(data.ancho * 2.54, 2);
      } else {
        if (data.peso) data.peso = Math.ceil(data.peso * 2.205);
      }

      data.idTipoPaquete = props.idTipoPaquete;

      const controller = new AbortController();
      const result = await createPackage(data, controller.signal);

      if (result) {
        if (result.code === 200) {
          if (result.payload) {
            if (result.payload.Procesado === "1") {
              modalContext.confirm(
                translate(
                  "dashboard.packages.loadpackage.formProvider.successTittle"
                ),
                result.payload.PackageNumber ? (
                  <div>
                    {translate(
                      "dashboard.packages.loadpackage.formProvider.successInfo"
                    )}
                    <CopyPackageNumber
                      packageNumber={result.payload.PackageNumber}
                    />
                  </div>
                ) : (
                  translate(
                    "dashboard.packages.loadpackage.formProvider.successInfo"
                  )
                ),
                <Button
                  type="button"
                  variant="secondary" size="sm"
                  onClick={() => modalContext.setIsOpenModal(false)}
                >
                  {translate("global.accept")}
                </Button>,
                true
              );
              resetFormB2BEmprendedores();
            }
            if (result.payload.Mensaje === "RESERVATION MADE SUCCESSFULLY") {
              modalContext.confirm(
                translate(
                  "dashboard.packages.loadpackage.formProvider.successTittle"
                ),
                <div>
                  {translate(
                    "dashboard.packages.loadpackage.formProvider.successInfoReserved"
                  )}
                  <div>
                    {translate("global.status.reservedDoor")} :{" "}
                    <span style={{ fontWeight: "bold" }}>
                      {result.payload.Locker}
                    </span>
                  </div>
                </div>,
                <Button
                  type="button"
                  variant="secondary" size="sm"
                  onClick={() => modalContext.setIsOpenModal(false)}
                >
                  {translate("global.accept")}
                </Button>,
                true
              );
              resetFormB2BEmprendedores();
            } else {
              const errorHandlers = {
                "El número de paquete ya se encuentra registrado.": () =>
                  setError("paquete", {
                    type: "",
                    message: translate(
                      "dashboard.packages.loadpackages.formProvider.packageRegistered"
                    ),
                  }),
                "El número de tracking ya se encuentra registrado.": () =>
                  setError("tracking", {
                    type: "",
                    message: translate(
                      "dashboard.packages.loadpackages.formProvider.trackingRegistered"
                    ),
                  }),
                "No tienes autorización para entregar en este Boxit.": () =>
                  setError("plataforma", {
                    type: "",
                    message: translate(
                      "dashboard.packages.loadpackages.formProvider.notAuthorized"
                    ),
                  }),
              };

              const errorMessage = result.payload.Mensaje;
              if (errorHandlers[errorMessage]) {
                errorHandlers[errorMessage]();
              }
            }
          } else
            modalContext.confirm(
              translate("global.alerts.ops"),
              translate(
                "dashboard.packages.loadpackages.formProvider.errorRegisterPackage"
              ),
              <Button
                type="button"
                variant="outline-secondary" size="sm"
                onClick={() => modalContext.setIsOpenModal(false)}
              >
                {translate("global.accept")}
              </Button>,
              true
            );
        } else
          modalContext.confirm(
            translate("global.alerts.ops"),
            translate(
              "dashboard.packages.loadpackages.formProvider.errorRegisterPackage"
            ),
            <Button
              type="button"
              variant="outline-secondary" size="sm"
              onClick={() => modalContext.setIsOpenModal(false)}
            >
              {translate("global.accept")}
            </Button>,
            true
          );
      }
    } catch (err) {
      console.log(err);
      modalContext.confirm(
        translate("global.alerts.ops"),
        translate(
          "dashboard.packages.loadpackages.formProvider.errorRegisterPackage"
        ),
        <Button
          type="button"
          variant="outline-secondary" size="sm"
          onClick={() => modalContext.setIsOpenModal(false)}
        >
          {translate("global.accept")}
        </Button>,
        true
      );
    } finally {
      setIsSendingData(false);
    }
  };

  return (
    <>
      <form
        className="carga-b2b-emprendedores m-0 p-0"
        onSubmit={handleSubmit(sendPackage)}
      >
        {props?.canSearch && userContext.user?.IsAdmin && (
          <FormGrid className="mt-4">
            <Field
              span={6}
              id="selectProveedor"
              label={translate("global.business")}
              required
            >
              <ProviderList
                register={register}
                errors={errors}
                nameShow={translate("global.select")}
                idShow={props.idTipoUsuario}
                readOnly={false}
                setValue={setValue}
                valueId={"proveedor"}
                setProveedor={setProvidor}
                selectedOption={providor}
              />
            </Field>

            {providor && (
              <Field
                span={6}
                id="selectCliente"
                label={translate("global.client")}
                required
              >
                <ClientsSearchList
                  idUsuarioSelected={providor?.value}
                  errors={errors}
                  setError={setError}
                  clearErrors={clearErrors}
                  register={register}
                  idTipoCliente={props.idTipoCliente}
                  setSelectOption={setSelectedClient}
                  autoSelectClient
                  isRequired={true}
                />
              </Field>
            )}
          </FormGrid>
        )}

        <FormGrid className="mt-4">
          {userContext.user.IdTipoUsuario !== 3 && (
            <Field
              span={6}
              id="inputNumeroPaquete"
              name="paquete"
              label={translate("dashboard.packages.loadpackages.formProvider.numPackage")}
              required
              error={errors.paquete}
              inputRef={register({
                required: {
                  value: true,
                  message: translate("global.error.notValidPackages"),
                },
              })}
            />
          )}

          <Field
            span={6}
            id="inputNumeroTracking"
            name="tracking"
            label={translate("dashboard.packages.loadpackages.formProvider.numTracking")}
            required
            error={errors.tracking}
            inputRef={register({
              required: {
                value: true,
                message: translate(
                  "dashboard.packages.loadpackages.formProvider.notValidTracking"
                ),
              },
            })}
          />

          <Field
            span={6}
            type="email"
            id="inputEmail"
            name="email"
            label={translate("global.email")}
            required
            icon={<MdIcons.MdEmail size={18} />}
            error={errors.email}
            inputRef={register({
              required: {
                value: true,
                message: translate(
                  "dashboard.packages.loadpackages.formProvider.emailValidate"
                ),
              },
              maxLength: {
                value: 60,
                message: translate(
                  "dashboard.userprofile.personal.email_max_length"
                ),
              },
            })}
          />
        </FormGrid>

        <FormGrid className="mt-4">
          <Field
            as="textarea"
            rows="2"
            id="inputDescripcion"
            name="descripcion"
            label={translate("global.description")}
            hint={`(${translate("global.optional")})`}
            inputRef={register}
          />

          <SwitchField
            id="inputTipoIngresoMedidas"
            label={translate(
              "dashboard.packages.loadpackages.formProvider.packageMeasures"
            )}
            checked={ingresarMedidas}
            onCheckedChange={setIngresarMedidas}
          />
        </FormGrid>

        <FormGrid className="mt-4">
          {ingresarMedidas && (
            <Field
              span={3}
              as="select"
              id="selectSistemaMedida"
              label={translate("global.units")}
              onChange={(e) => setUnidad(e.target.value === "cm / kg" ? 0 : 1)}
            >
              <option>inch / lb</option>
              <option>cm / kg</option>
            </Field>
          )}

          <Field
            span={3}
            type="number"
            id="inputPeso"
            name="peso"
            placeholder="0"
            label={`${translate("global.weight")} (${unidad === 0 ? "kg" : "lb"})`}
            hint={`(${translate("global.optional")})`}
            error={errors.peso}
            inputRef={register}
          />

          {!ingresarMedidas && (
            <Field
              span={3}
              as="select"
              id="selectTamano"
              name="size"
              label={translate("global.size")}
              inputRef={register()}
            >
              <option>P</option>
              <option>M</option>
              <option>G</option>
            </Field>
          )}
        </FormGrid>

        {ingresarMedidas && <FieldSizes register={register} unidad={unidad} />}

        <FormGrid className="mt-4">
          <Field span={6} id="selectBoxit" label="Boxit" required>
            <PlatformSearchList
              register={register}
              errors={errors}
              setPlatformSelect={setPlatformSelect}
              platfSelect={platformSelect}
              valueId={"plataforma"}
              readOnly={false}
              setValue={setValue}
              idPlatform={idPlatform.current}
              handleIsBoxit={handleIsBoxit}
              defaultPlatformId={props.idPlatformSelect}
            />
          </Field>

          {((platformSelect && platformSelect?.tipoBoxit === 1) ||
            userContext.user?.IsAdmin) &&
            userRole >= 3 &&
            isBoxit &&
            idPlatform.current !== translate("global.select") &&
            idPlatform.current !== "" && (
              <Field
                span={6}
                id="selectPuertas"
                label={translate(
                  "dashboard.packages.loadpackages.formProvider.doorsAvailable"
                )}
              >
                <DoorList
                  errors={errors}
                  register={register}
                  nameShow={translate("global.select")}
                  idShow={idPlatform.current}
                  readOnly={false}
                  isBoxit
                />
              </Field>
            )}
        </FormGrid>

        {(isBoxit || userContext.user?.IsAdmin) && (
          <FormGrid className="mt-4">
            <Field
              span={3}
              type="number"
              step=".01"
              id="inputMontoPagar"
              name="montoPagar"
              placeholder="0"
              label={
                <>
                  {translate(
                    "dashboard.packages.loadpackages.formProvider.amount"
                  )}{" "}
                  (B/)
                  <Tooltip
                    info={translate(
                      "dashboard.packages.loadpackages.formProvider.amount.info"
                    )}
                    className="ml-1"
                  />
                </>
              }
              error={errors.montoPagar}
              inputRef={register}
            />
          </FormGrid>
        )}

        <div className="mt-6 flex flex-col-reverse justify-center gap-3 sm:flex-row">
          <Button
            type="reset"
            variant="outline-secondary"
            onClick={() => resetFormB2BEmprendedores()}
          >
            {translate("global.clean")}
          </Button>
          {props?.canCreate && (
            <Button type="submit" variant="secondary" disabled={isSendingData}>
              {translate("global.create")}
            </Button>
          )}
        </div>

        <LockerSizes unidad={unidad} />
      </form>
    </>
  );
};

export default FormB2BEmprendedores;
